import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { API_BASE_URL } from '../../core/api-base-url.token';
import { ForecastDto, ForecastService } from '../../core/services/forecast.service';

export interface TimeSlot {
  time: string;
  minutes: number;
}

export interface HistoricalScansResponse {
  queueId: string;
  contextId: string | null;
  contextName?: string;
  dayOfWeek: number;
  periods?: HistoricalPeriod[];
  points: HistoricalScansPoint[];
}

export interface HistoricalScansPoint {
  time: string;
  avgScans: number;
  periodAvgs: { periodId: string; avgScans: number }[];
}

export interface HistoricalPeriod {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
}

@Injectable({ providedIn: 'root' })
export class AjustementManuelService {
  private http = inject(HttpClient);
  private base = inject(API_BASE_URL);
  private forecastService = inject(ForecastService);

  getAdjustmentData(queueId: string, dayOfWeek: number): Observable<TimeSlot[]> {
    return this.forecastService.getDayBaseline(queueId, undefined, dayOfWeek).pipe(
      map((forecast: ForecastDto) => forecast.points || [])
    );
  }

  saveManualForecast(queueId: string, forecastDate: string, points: TimeSlot[]): Observable<any> {
    const url = `${this.base}/api/v1/queues/${encodeURIComponent(queueId)}/manual-forecast`;
    return this.http.post<any>(url, {
      forecastDate,
      points
    });
  }

  deleteManualForecast(queueId: string, date: string): Observable<any> {
    const url = `${this.base}/api/v1/queues/${encodeURIComponent(queueId)}/manual-forecast`;
    const params = new HttpParams().set('date', date);
    return this.http.delete<any>(url, { params });
  }

  getHistoricalScans(queueId: string, dayOfWeek: number, contextId?: string): Observable<HistoricalScansResponse> {
    const url = `${this.base}/api/v1/queues/${encodeURIComponent(queueId)}/historical-scans`;
    let params = new HttpParams().set('dayOfWeek', dayOfWeek.toString());
    if (contextId) {
      params = params.set('contextId', contextId);
    }
    return this.http.get<any>(url, { params }).pipe(
      map(raw => ({
        queueId: raw.queueId || raw.queue_id,
        contextId: raw.contextId || raw.context_id,
        contextName: raw.contextName || raw.context_name,
        dayOfWeek: raw.dayOfWeek || raw.day_of_week,
        periods: (raw.periods || []).map((p: any) => ({
          id: p.id,
          name: p.name,
          startDate: p.startDate || p.start_date,
          endDate: p.endDate || p.end_date
        })),
        points: (raw.points || []).map((pt: any) => ({
          time: pt.time,
          avgScans: pt.avgScans || pt.avg_scans || 0,
          periodAvgs: (pt.periodAvgs || pt.period_avgs || []).map((pa: any) => ({
            periodId: pa.periodId || pa.period_id,
            avgScans: pa.avgScans || pa.avg_scans || 0
          }))
        }))
      }))
    );
  }
}
